<main id="main-content" class="h-screen flex flex-col pl-8 py-4 pr-4 transition-all duration-0">
    <div class="w-full flex flex-col p-2 items-center gap-2">
        <div class="w-full flex flex-row items-center justify-between">
            <div class="w-full flex gap-2 items-center justify-start">
                <h1 class="oswald-font text-3xl uppercase">
                    Form a collaborative planning team
                </h1>
                <div data-popover-target="popover-bottom" data-popover-placement="bottom" type="button">
                    <lucide-icon [name]="icons.CircleHelp" [size]=24 class="text-red-500"></lucide-icon>
                </div>
                <div data-popover id="popover-bottom" role="tooltip" class="absolute z-10 invisible inline-block w-[600px] text-sm text-gray-500 transition-opacity duration-300 bg-white border border-gray-200 rounded-md shadow-xs opacity-0">
                    <div class="px-3 py-2">
                        <p>
                            Using the drop-down menu on the right, select the department to which you would like to assign team members. Once you have selected the correct department, using the contacts on the left, select or search for contacts and click the 
                            <span class="inline-flex w-8 h-8 flex items-center justify-center rounded-md border bg-gradient-to-b border-nside-orange-100 from-nside-orange-300 to-nside-orange-400">
                                <lucide-icon [name]="icons.Plus" [size]="20" class="text-white"></lucide-icon>
                            </span> next to the contact's name to add them to the collaborative planning team.
                        </p>
                        <p>
                            If the button beside the contact appears as 
                            <span class="inline-flex w-8 h-8 flex items-center justify-center rounded-md border bg-gradient-to-b border-nside-gray-100 from-nside-gray-100 to-nside-gray-200">
                                <lucide-icon [name]="icons.Plus" [size]="20" class="text-white"></lucide-icon>
                            </span>, they have already been assigned to that department/position in the Core Planning Team.
                        </p>
                        <p>
                            **Remember to ALWAYS save your work by clicking on 
                            <span class="inline-flex px-3.5 py-1 flex items-center justify-center text-white oswald-font bg-gradient-to-b from-nside-orange-300 to-nside-orange-400 rounded-md">
                                Save
                            </span> every time a department/position is edited.
                        </p>
                        <p>
                            If you need to start fresh, simply click on 
                            <span 
                                class="inline-flex w-8 h-8 rounded border-2 border-nside-gray-200 text-nside-gray-200 flex items-center justify-center"
                            >
                                <lucide-icon [img]="icons.Eraser" size="20"></lucide-icon>
                            </span> This will clear all members assigned to that specific department/position. 
                        </p>
                        <p>
                            To view all people that are assigned to each department, click on 
                            <span 
                                class="inline-flex w-8 h-8 rounded border-2 border-nside-gray-200 text-nside-gray-200 flex items-center justify-center hover:text-white hover:bg-nside-gray-200"
                            >
                                <lucide-icon [img]="icons.FileText" size="20"></lucide-icon>
                            </span> A pop-up will appear allowing you to view every member assigned to each department/position.
                        </p>
                    </div>
                    <div data-popper-arrow></div>
                </div>
            </div>
            <eop-assistant
                [sectionId]="sectionType"
            ></eop-assistant>
        </div>
    </div>
    
    <!-- MAIN CONTENT -->
    <div class="p-2 w-full flex flex-grow flex-row items-start gap-8 overflow-y-auto">
        <div class="w-1/3 max-h-full bg-white rounded-md shadow-custom-2 p-6 flex flex-col gap-4">
            <div class="flex flex-row w-full items-center justify-between gap-2">
                <h2 class="oswald-font text-3xl font-medium w-1/2">CONTACTS</h2>
                <div class="relative w-1/2">
                    <div class="absolute inset-y-0 start-0 flex items-center ps-3 pointer-events-none">
                        <lucide-icon [name]="icons.SearchIcon" [size]="20"></lucide-icon>
                    </div>
                    <input 
                        type="search"
                        [(ngModel)]="searchQuery"
                        (input)="filterContacts()"
                        class="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full ps-10 p-2"
                        placeholder="Search" 
                        required noEmoji/>
                </div>
            </div>
            <div class="h-full w-full flex flex-wrap text-gray-900 overflow-y-auto">
                <div class="h-full w-full mr-2 flex flex-col gap-2">
                    <div *ngIf="loadingContacts">
                        <cards-list-skeleton></cards-list-skeleton>
                    </div>
                    <ng-container *ngIf="!loadingContacts">
                        <ng-container *ngIf="contactsList.length > 0; else emptyContact">                        
                            <div 
                                *ngFor="let contact of contactsList"
                                class="w-full flex flex-row items-stretch gap-2"
                            >
                                <button
                                    class="min-h-10 flex-1 px-2.5 py-1.5 flex flex-row gap-2 items-center justify-between rounded-md bg-gray-50 border border-nside-gray-300 hover:bg-gradient-to-b hover:from-nside-orange-100 hover:to-nside-orange-200 hover:text-white hover:border-nside-orange-100 group"
                                    (click)="$event.preventDefault(); openInfo(contact);"
                                >
                                    <span class="text-start">{{ contact.name }}</span>
                                </button>
                                <button
                                    *ngIf="canSave()"
                                    [disabled]="isContactSelected(contact)"
                                    class="w-10 h-10 flex items-center justify-center rounded-md border bg-gradient-to-b"
                                    [ngClass]="{
                                        'border-nside-gray-100 from-nside-gray-100 to-nside-gray-200 cursor-not-allowed': isContactSelected(contact),
                                        'border-nside-orange-100 from-nside-orange-300 to-nside-orange-400 hover:bg-gradient-to-b hover:from-nside-orange-100 hover:to-nside-orange-200': !isContactSelected(contact)
                                        }"
                                    (click)="$event.preventDefault(); addContactToDepartment(contact);"
                                >
                                    <lucide-icon [name]="icons.Plus" [size]="20" class="text-white"></lucide-icon>
                                </button>
                            </div>
                        </ng-container>
                    </ng-container>                                 
                    <ng-template #emptyContact>
                        <div class="text-nside-gray-100 italic">
                            Empty list
                        </div>
                    </ng-template>
                </div>
            </div>                
        </div>
        <div class="w-2/3 max-h-full bg-white rounded-md shadow-custom-2 p-6 flex flex-col gap-4">
            <div class="w-full flex items-center justify-between">
                <h2 class="oswald-font text-3xl">DEPARTMENT</h2> 
                <div class="flex items-center justify-start gap-1">
                    <button 
                        *ngIf="canSave()"
                        data-tooltip-target="tooltip-delete"  data-tooltip-placement="bottom"
                        class="w-8 h-8 rounded border-2 border-nside-gray-200 text-nside-gray-200 flex items-center justify-center"
                        [ngClass]="{ 
                            'hover:text-white hover:bg-nside-gray-200': selectedContacts.length > 0,
                            'cursor-not-allowed opacity-50': selectedContacts.length === 0 
                        }"
                        (click)="$event.preventDefault(); selectedContacts = [];"
                        [disabled]="selectedContacts.length === 0"
                    >
                        <lucide-icon [img]="icons.Eraser" size="20"></lucide-icon>
                    </button>
                    <div id="tooltip-delete" role="tooltip" class="absolute z-10 invisible inline-block px-3 py-2 text-sm font-medium text-white bg-gray-600 rounded-lg shadow-sm opacity-0 tooltip">
                        Clear All Fields
                        <div class="tooltip-arrow" data-popper-arrow></div>
                    </div>
                    <button 
                        data-tooltip-target="tooltip-planning-team"  data-tooltip-placement="bottom"
                        class="w-8 h-8 rounded border-2 border-nside-gray-200 text-nside-gray-200 flex items-center justify-center hover:text-white hover:bg-nside-gray-200"
                        (click)="$event.preventDefault(); handleOpenModal('planning-team');"
                    >
                        <lucide-icon [img]="icons.FileText" size="20"></lucide-icon>
                    </button>
                    <div id="tooltip-planning-team" role="tooltip" class="absolute z-10 invisible inline-block px-3 py-2 text-sm font-medium text-white bg-gray-600 rounded-lg shadow-sm opacity-0 tooltip">
                        View Planning Team
                        <div class="tooltip-arrow" data-popper-arrow></div>
                    </div>
                </div>
            </div>           
            <div class="w-screen-md flex flex-col gap-2 group">
                <select 
                    id="departments" 
                    class="border border-nside-gray-300 font-medium rounded-md focus:ring-nside-gray-300 focus:border-nside-gray-300 block w-full p-2.5"
                    [(ngModel)]="selectedDepartmentId" 
                    (change)="onDepartmentChange($event)"
                >
                    <option *ngFor="let dept of departmentsList" [value]="dept.id">
                        {{ dept.department }}
                    </option>
                </select>
            </div>
            <div class="w-full p-4 min-h-32 max-h-full rounded-md border-2 border-nside-gray-300 flex flex-grow overflow-y-auto">
                <div class="h-full flex flex-col gap-2 text-gray-900 overflow-y-auto">
                    <ng-container *ngIf="selectedContacts.length > 0; else emptySelectedContact">            
                        <div 
                            *ngFor="let selectedContact of selectedContacts"
                            class="w-full flex flex-row items-stretch gap-2"
                        >
                            <button
                                class="flex-1 px-2.5 py-1.5 flex flex-row gap-2 items-center rounded-md bg-gray-50 border border-nside-gray-300 hover:bg-gradient-to-b hover:from-nside-orange-100 hover:to-nside-orange-200 hover:text-white hover:border-nside-orange-100"
                                (click)="$event.preventDefault(); openInfo(selectedContact);"
                            >
                                {{ selectedContact.name }}
                            </button>
                            <button
                                class="h-10 w-10 flex flex-row gap-2 items-center justify-center rounded-md border border-nside-gray-100 bg-gradient-to-b from-nside-gray-100 to-nside-gray-200 hover:bg-gradient-to-b hover:from-nside-gray-300 hover:to-nside-gray-400"
                                (click)="$event.preventDefault(); removeContactFromDepartment(selectedContact);"
                            >
                                <lucide-icon [name]="icons.X" [size]="20" class="text-white"></lucide-icon>
                            </button>
                        </div>
                    </ng-container>
                    <ng-template #emptySelectedContact>
                        <div class="text-nside-gray-100 italic">
                            Assign contact(s) to department
                        </div>
                    </ng-template>
                </div>
            </div>
            <div class="flex items-end justify-end">
                <button 
                    *ngIf="canSave()"
                    class="w-20 px-5 py-1.5 flex items-center justify-center text-white oswald-font bg-gradient-to-b from-nside-orange-300 to-nside-orange-400 rounded-md hover:bg-gradient-to-b hover:from-nside-orange-100 hover:to-nside-orange-200"
                    (click)="saveSelectedContacts();"
                >
                    Save
                </button>
            </div>
        </div>
    </div>
    
    <div *ngIf="loading">
        <spinner></spinner>
    </div>

    <introduction></introduction>

    <planning-team-details
        *ngIf="showPlanningTeamModal"
        [planningTeam]="allPlanningTeam"
        [contactsList]="contactsList"
        [departmentsList]="departmentsList"
        (onClose)="handleCloseModal('planning-team')"
    ></planning-team-details>
    
    <contact-info-popup
        *ngIf="contactInfo"
        [contact]="contactInfo"
    ></contact-info-popup>

    <popup-message 
        [type]="messageModal"
        [message]="popupMessage"
        (close)="resetMessageModal()"
    ></popup-message>

    <prv-next-buttons [totalPages]="(eopNavigationService.totalPages$ | async) || []" />

</main>